/**
 * NCB API Client — Definitive Fix
 */
import bcrypt from 'bcryptjs';

const getEnv = (key) => {
  const val = import.meta.env[key];
  if (!val) console.warn(`[NCB] Missing env var: ${key}`);
  return val || '';
};

const INSTANCE   = getEnv('VITE_NCB_INSTANCE');
const SECRET_KEY = getEnv('VITE_NCB_SECRET_KEY');
const DATA_URL   = 'https://app.nocodebackend.com/api/data';

// ─── Session ──────────────────────────────────────────────────────────────────
export const getSession = () => {
  try {
    const s = localStorage.getItem('ncb_user');
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

// ─── Core data fetch — always SECRET_KEY ──────────────────────────────────────
const dataFetch = async (path, options = {}) => {
  const sep = path.includes('?') ? '&' : '?';
  const url  = `${DATA_URL}/${path}${sep}Instance=${INSTANCE}`;

  console.debug(`[NCB] ${options.method || 'GET'} ${url}`, options.body || '');

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SECRET_KEY}`,
      'X-Database-Instance': INSTANCE,
      ...options.headers,
    },
  });

  let data;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    data = await res.json();
  } else {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }

  if (!res.ok) {
    console.error('[NCB] API error response:', data);
    throw new Error(data?.message || data?.error || `API Error ${res.status}`);
  }

  return data;
};

const unwrap = (res) => {
  if (res && res.data !== undefined) return res.data;
  return res;
};



// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authApi = {
  signIn: async (email, password) => {
    const res   = await dataFetch(`read/users_admin?email=${encodeURIComponent(email)}&limit=1`);
    const users = unwrap(res);
    const user  = Array.isArray(users) ? users[0] : users;

    if (!user) throw new Error('Invalid email or password');

    let isValid      = false;
    let needsUpgrade = false;

    if (user.password && user.password.startsWith('$2')) {
      isValid = await bcrypt.compare(password, user.password);
    } else {
      isValid      = (user.password === password);
      needsUpgrade = true;
    }

    if (!isValid) throw new Error('Invalid email or password');

    if (needsUpgrade) {
      const hashed = await bcrypt.hash(password, 10);
      const updateId = user.id || user.owner_id;
      if (updateId) {
        dataFetch(`update/users_admin/${updateId}`, {
          method: 'PUT',
          body: JSON.stringify({ password: hashed }),
        }).catch(() => {});
      }
    }

    const session = {
      id:      user.owner_id,
      ownerId: user.owner_id,
      email:   user.email,
      name:    user.name || 'Admin',
      role:    user.role  || 'admin',
    };

    localStorage.setItem('ncb_user', JSON.stringify(session));
    return session;
  },

  logout: () => {
    localStorage.removeItem('ncb_token');
    localStorage.removeItem('ncb_user');
  },
};

// ─── SITES ────────────────────────────────────────────────────────────────────
export const sitesApi = {
  getAll: async () => {
    const user = getSession();
    if (!user) return [];
    const res = await dataFetch(`read/sites?owner_id=${encodeURIComponent(user.ownerId)}&limit=100`);
    return unwrap(res) || [];
  },

  getById: async (id) => {
    const res = await dataFetch(`read/sites/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const user = getSession();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      site_name:      String(data.site_name || '').trim(),
      custom_domain:  String(data.custom_domain || '').trim(),
      meta_pixel_id:  String(data.meta_pixel_id || '').trim(),
      ga_tracking_id: String(data.ga_tracking_id || '').trim(),
      owner_id:       user.ownerId,
    };

    return dataFetch('create/sites', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  update: async (id, data) => {
    const payload = {};
    if (data.site_name !== undefined) payload.site_name = String(data.site_name).trim();
    if (data.custom_domain !== undefined) payload.custom_domain = String(data.custom_domain).trim();
    if (data.meta_pixel_id !== undefined) payload.meta_pixel_id = String(data.meta_pixel_id).trim();
    if (data.ga_tracking_id !== undefined) payload.ga_tracking_id = String(data.ga_tracking_id).trim();
    
    return dataFetch(`update/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  delete: (id) => dataFetch(`delete/sites/${id}`, { method: 'DELETE' }),
};

// ─── PAGES ────────────────────────────────────────────────────────────────────
export const pagesApi = {
  getAll: async () => {
    const user = getSession();
    if (!user) return [];
    const res = await dataFetch(`read/pages?owner_id=${encodeURIComponent(user.ownerId)}&limit=500`);
    return unwrap(res) || [];
  },

  getById: async (id) => {
    const res = await dataFetch(`read/pages/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const user = getSession();
    if (!user) throw new Error('Not authenticated');

    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);

    const payload = {
      site_id:      data.site_id ? Number(data.site_id) : null,
      title:        String(data.title || '').trim(),
      slug:         String(data.slug  || '').trim(),
      page_type:    data.page_type || 'sales',
      status:       data.status    || 'draft',
      html_content: data.html_content || '',
      owner_id:     user.ownerId,
      created_at:   now,
      updated_at:   now,
    };

    return dataFetch('create/pages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  update: async (id, data) => {
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
    const allowed = ['site_id', 'title', 'slug', 'html_content', 'status', 'page_type'];
    const payload = { updated_at: now };

    allowed.forEach((key) => {
      if (data[key] !== undefined) {
        payload[key] = key === 'site_id' ? Number(data[key]) : data[key];
      }
    });

    return dataFetch(`update/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  delete: (id) => dataFetch(`delete/pages/${id}`, { method: 'DELETE' }),
};

// ─── ANALYTICS ────────────────────────────────────────────────────────────────
export const analyticsApi = {
  getAll: async () => {
    const user = getSession();
    if (!user) return [];
    const res = await dataFetch(`read/page_analytics?owner_id=${encodeURIComponent(user.ownerId)}&limit=5000`);
    return unwrap(res) || [];
  }
};